import React, { useEffect, useRef } from 'react';

export interface TelegramUser {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

interface TelegramLoginWidgetProps {
  botName: string;
  onAuth: (user: TelegramUser) => void;
  buttonSize?: 'large' | 'medium' | 'small';
  cornerRadius?: number;
  requestAccess?: 'write';
}

/**
 * Telegram Login Widget Component
 * 
 * Renders the official Telegram Login Widget for browser-based authentication.
 * This is used when the miniapp is accessed from a regular browser instead of Telegram.
 */
const TelegramLoginWidget: React.FC<TelegramLoginWidgetProps> = ({
  botName,
  onAuth,
  buttonSize = 'large',
  cornerRadius = 8,
  requestAccess = 'write',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const callbackNameRef = useRef<string>('');

  useEffect(() => {
    if (!botName || !containerRef.current) return;

    // Generate unique callback name
    const callbackName = `onTelegramAuth_${Math.random().toString(36).slice(2)}`;
    callbackNameRef.current = callbackName;

    // Register callback globally
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any)[callbackName] = (user: TelegramUser) => {
      onAuth(user);
    };

    // Clear container
    containerRef.current.innerHTML = '';

    // Create and inject the Telegram widget script
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', botName);
    script.setAttribute('data-size', buttonSize);
    script.setAttribute('data-radius', cornerRadius.toString());
    script.setAttribute('data-onauth', `${callbackName}(user)`);
    if (requestAccess) {
      script.setAttribute('data-request-access', requestAccess);
    }

    containerRef.current.appendChild(script);

    // Cleanup
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (window as any)[callbackName];
    };
  }, [botName, onAuth, buttonSize, cornerRadius, requestAccess]);

  if (!botName) {
    return (
      <p className="text-text-secondary text-sm">
        Telegram bot is not configured. Please check VITE_TELEGRAM_BOT_USERNAME.
      </p>
    );
  }

  return <div ref={containerRef} className="flex justify-center" />;
};

export default TelegramLoginWidget;

